// src/pages/Login.jsx
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const DAR_COLORS = {
  cream: "#F5F0E8",
  warmBeige: "#EDE5D5",
  gold: "#C9A96E",
  goldLight: "#E8D5A3",
  goldDark: "#9E7A3F",
  deepGreen: "#1E3A2F",
  midGreen: "#2D5A40",
  textDark: "#2C2416",
  textMid: "#6B5A3E",
  textLight: "#A89070",
  white: "#FDFAF5",
};

const API = import.meta.env.VITE_API_URL || "https://daroulmesbackend.onrender.com";
const getToken = () => localStorage.getItem("access_token") || "";

async function apiFetch(path, opts = {}) {
  const headers = { Authorization: `Bearer ${getToken()}`, ...opts.headers };
  if (!(opts.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }
  const res = await fetch(`${API}${path}`, { ...opts, headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || err.message || res.statusText);
  }
  if (res.status === 204) return null;
  return res.json();
}

const api = {
  login: (body) => apiFetch("/api/auth/login", { method: "POST", body: JSON.stringify(body) }),
};

const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=Jost:wght@300;400;500&display=swap');

  * { box-sizing: border-box; margin: 0; padding: 0; }

  @keyframes fadeSlideUp {
    from { opacity: 0; transform: translateY(32px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn {
    from { opacity: 0; } to { opacity: 1; }
  }
  @keyframes floatGold {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50%       { transform: translateY(-18px) rotate(3deg); }
  }
  @keyframes shimmer {
    0%   { background-position: -200% center; }
    100% { background-position: 200% center; }
  }
  @keyframes spin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
  @keyframes spinReverse {
    from { transform: rotate(0deg); }
    to   { transform: rotate(-360deg); }
  }
  @keyframes pulseGlow {
    0%, 100% { box-shadow: 0 0 0 0 rgba(201,169,110,0.0); }
    50%       { box-shadow: 0 0 24px 6px rgba(201,169,110,0.18); }
  }

  .login-root {
    min-height: 100vh;
    display: flex;
    position: relative;
    overflow: hidden;
    flex-direction: row;
  }
  .login-brand-panel {
    width: 48%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 48px;
    position: relative;
    overflow: hidden;
    flex-shrink: 0;
  }
  .login-form-panel {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 60px 48px;
    position: relative;
  }
  .login-form-inner {
    width: 100%;
    max-width: 420px;
  }
  .login-brand-pills {
    display: flex;
    gap: 10px;
    margin-top: 52px;
    position: relative;
    z-index: 1;
    flex-wrap: wrap;
    justify-content: center;
  }

  @media (max-width: 900px) {
    .login-root { flex-direction: column; }
    .login-brand-panel {
      width: 100%;
      padding: 40px 24px 36px;
      min-height: unset;
    }
    .login-brand-pills { margin-top: 24px; }
    .login-form-panel { padding: 32px 24px 48px; }
  }

  @media (max-width: 480px) {
    .login-brand-panel { padding: 32px 20px 28px; }
    .login-form-panel  { padding: 24px 20px 40px; }
    .login-form-inner  { max-width: 100%; }
  }
`;

function StarPattern({ style: extraStyle, opacity = 0.12, size = 220, animate = false, speed = 20 }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 220 220"
      style={{
        position: "absolute", opacity,
        animation: animate ? `floatGold ${speed}s ease-in-out infinite` : undefined,
        pointerEvents: "none",
        ...extraStyle,
      }}
      xmlns="http://www.w3.org/2000/svg"
    >
      <g stroke={DAR_COLORS.gold} strokeWidth="0.8" fill="none">
        {[0,30,60,90,120,150].map(a => (
          <line key={a} x1="110" y1="110"
            x2={110 + 100 * Math.cos(a * Math.PI / 180)}
            y2={110 + 100 * Math.sin(a * Math.PI / 180)}
          />
        ))}
        <circle cx="110" cy="110" r="100" strokeDasharray="4 6" />
        <circle cx="110" cy="110" r="70" />
        <circle cx="110" cy="110" r="45" strokeDasharray="2 4" />
        {[0,45,90,135,180,225,270,315].map(a => (
          <circle key={a}
            cx={110 + 70 * Math.cos(a * Math.PI / 180)}
            cy={110 + 70 * Math.sin(a * Math.PI / 180)}
            r="4"
          />
        ))}
        <polygon points="110,20 118,80 110,95 102,80" />
        <polygon points="110,200 118,140 110,125 102,140" />
        <polygon points="20,110 80,102 95,110 80,118" />
        <polygon points="200,110 140,102 125,110 140,118" />
      </g>
    </svg>
  );
}

function GoldWave({ style: extraStyle }) {
  return (
    <svg viewBox="0 0 1200 120" preserveAspectRatio="none"
      style={{ position: "absolute", width: "100%", pointerEvents: "none", ...extraStyle }}>
      <path d="M0,60 C150,100 350,0 600,60 C850,120 1050,20 1200,60 L1200,120 L0,120 Z" fill={`${DAR_COLORS.gold}22`} />
      <path d="M0,80 C200,40 400,100 600,80 C800,60 1000,100 1200,80 L1200,120 L0,120 Z" fill={`${DAR_COLORS.gold}12`} />
    </svg>
  );
}

// ── Google OAuth button ──────────────────────────────────────────────────────
function GoogleButton({ mounted, delay, label }) {
  const [hovered, setHovered] = useState(false);

  const handleGoogleLogin = () => {
    // Redirect to the server-side Google OAuth initiation endpoint
    window.location.href = `${API}/api/auth/google`;
  };

  return (
    <button
      type="button"
      onClick={handleGoogleLogin}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: "100%",
        padding: "14px 24px",
        background: hovered ? "#f5f5f5" : DAR_COLORS.white,
        border: `1.5px solid ${hovered ? DAR_COLORS.gold : `${DAR_COLORS.gold}40`}`,
        borderRadius: 10,
        color: DAR_COLORS.textDark,
        fontSize: 13,
        fontFamily: "'Jost', sans-serif",
        fontWeight: 400,
        letterSpacing: 0.5,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        transition: "all 0.3s ease",
        boxShadow: hovered ? `0 4px 16px ${DAR_COLORS.gold}20` : "0 2px 8px rgba(0,0,0,0.06)",
        opacity: mounted ? 1 : 0,
        transform: mounted ? "translateY(0)" : "translateY(16px)",
      }}
    >
      {/* Google "G" SVG icon */}
      <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
      </svg>
      {label}
    </button>
  );
}

export default function Login({ onNavigateToRegister, onAuthSuccess }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { setTimeout(() => setMounted(true), 50); }, []);

  // Handle Google OAuth callback token in URL (after redirect back from server)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const userRaw = params.get("user");
    if (token) {
      try {
        const user = userRaw ? JSON.parse(decodeURIComponent(userRaw)) : null;
        // FIX: use "access_token" consistently everywhere
        localStorage.setItem("access_token", token);
        if (user) localStorage.setItem("user", JSON.stringify(user));
        // Clean the URL
        window.history.replaceState({}, document.title, window.location.pathname);
        if (typeof onAuthSuccess === "function") {
          onAuthSuccess({ token, user });
        }
      } catch {
        setError("Google sign-in failed. Please try again.");
      }
    }
  }, [onAuthSuccess]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError(t("login.error_required", { defaultValue: "Email and password are required" }));
      return;
    }

    setLoading(true);
    try {
      const data = await api.login({ email, password });

      // FIX: use "access_token" consistently (matches getToken() above)
      localStorage.setItem("access_token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));

      if (typeof onAuthSuccess === "function") {
        onAuthSuccess({ token: data.token, user: data.user });
      }
    } catch (err) {
      setError(err.message || t("login.error_network", { defaultValue: "Network error. Please try again." }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{globalStyles}</style>
      <div className="login-root" style={{
        background: `linear-gradient(160deg, ${DAR_COLORS.warmBeige} 0%, ${DAR_COLORS.cream} 50%, #EDE8DC 100%)`,
        fontFamily: "'Jost', sans-serif",
      }}>
        <StarPattern style={{ top: -60, left: -60 }} size={280} animate opacity={0.10} speed={25} />
        <StarPattern style={{ bottom: -80, right: -80 }} size={320} animate opacity={0.08} speed={30} />
        <StarPattern style={{ top: "40%", right: "8%"  }} size={160} animate opacity={0.07} speed={20} />
        <GoldWave style={{ bottom: 0, left: 0, height: 140 }} />

        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 3, zIndex: 10,
          background: `linear-gradient(90deg, transparent, ${DAR_COLORS.gold}, ${DAR_COLORS.goldLight}, ${DAR_COLORS.gold}, transparent)`,
          backgroundSize: "200% 100%",
          animation: "shimmer 3s linear infinite",
        }} />

        {/* Brand Panel */}
        <div
          className="login-brand-panel"
          style={{
            background: `linear-gradient(145deg, ${DAR_COLORS.deepGreen} 0%, ${DAR_COLORS.midGreen} 100%)`,
            opacity: mounted ? 1 : 0,
            transform: mounted ? "translateX(0)" : "translateX(-40px)",
            transition: "all 0.9s cubic-bezier(0.23,1,0.32,1)",
          }}
        >
          <div style={{ position: "absolute", inset: 0, opacity: 0.08, pointerEvents: "none" }}>
            <StarPattern style={{ top: -40, left: -40 }} size={260} opacity={1} animate speed={40} />
            <StarPattern style={{ bottom: -60, right: -60 }} size={300} opacity={1} animate speed={35} />
          </div>

          <div style={{
            position: "absolute", top: 0, right: 0, width: 3, height: "100%",
            background: `linear-gradient(180deg, transparent, ${DAR_COLORS.gold}, ${DAR_COLORS.goldLight}, ${DAR_COLORS.gold}, transparent)`,
            backgroundSize: "100% 200%",
            animation: "shimmer 4s linear infinite",
          }} />

          <div style={{ textAlign: "center", marginBottom: 40, position: "relative", zIndex: 1 }}>
            <div style={{
              width: 150, height: 150, borderRadius: "50%",
              border: `2px solid ${DAR_COLORS.gold}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 20px", position: "relative",
              animation: "pulseGlow 3s ease-in-out infinite",
            }}>
              <img src="/logo 1.png" alt="Dar Oulmes" style={{ width: 140, height: 140, borderRadius: "50%", objectFit: "cover" }} />
              <div style={{
                position: "absolute", inset: -8, borderRadius: "50%",
                border: `1px solid ${DAR_COLORS.gold}44`,
                borderTopColor: DAR_COLORS.gold,
                animation: "spin 8s linear infinite",
              }} />
              <div style={{
                position: "absolute", inset: -16, borderRadius: "50%",
                border: `1px solid ${DAR_COLORS.gold}22`,
                borderBottomColor: `${DAR_COLORS.gold}88`,
                animation: "spinReverse 12s linear infinite",
              }} />
            </div>

            <div style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 34, fontWeight: 300, letterSpacing: 6,
              color: DAR_COLORS.white, textTransform: "uppercase", lineHeight: 1, marginBottom: 6,
            }}>
              Dar Oulmes
            </div>
            <div style={{
              fontSize: 10, letterSpacing: 4, color: DAR_COLORS.goldLight,
              textTransform: "uppercase", fontWeight: 300,
            }}>
              {t("brand.tagline")}
            </div>
          </div>

          <div style={{
            width: 60, height: 1,
            background: `linear-gradient(90deg, transparent, ${DAR_COLORS.gold}, transparent)`,
            marginBottom: 36,
          }} />

          <div style={{ textAlign: "center", position: "relative", zIndex: 1 }}>
            <p style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 21, fontWeight: 300, fontStyle: "italic",
              color: `${DAR_COLORS.white}CC`, lineHeight: 1.7, marginBottom: 18,
              maxWidth: 300,
            }}>
              "{t("brand.quote")}"
            </p>
            <p style={{
              fontSize: 11, color: `${DAR_COLORS.goldLight}99`,
              letterSpacing: 2, textTransform: "uppercase",
            }}>
              {t("brand.collection")}
            </p>
          </div>

          <div className="login-brand-pills">
            {(t("brand.pills", { returnObjects: true }) || []).map((tag, i) => (
              <div key={tag} style={{
                padding: "6px 14px",
                border: `1px solid ${DAR_COLORS.gold}44`,
                borderRadius: 20,
                fontSize: 10, letterSpacing: 2,
                color: DAR_COLORS.goldLight,
                textTransform: "uppercase",
                opacity: mounted ? 1 : 0,
                transform: mounted ? "translateY(0)" : "translateY(16px)",
                transition: `all 0.6s cubic-bezier(0.23,1,0.32,1) ${0.5 + i * 0.1}s`,
              }}>
                {tag}
              </div>
            ))}
          </div>
        </div>

        {/* Form Panel */}
        <div className="login-form-panel">
          <div
            className="login-form-inner"
            style={{
              opacity: mounted ? 1 : 0,
              transform: mounted ? "translateY(0)" : "translateY(40px)",
              transition: "all 1s cubic-bezier(0.23,1,0.32,1) 0.2s",
            }}
          >
            <div style={{ marginBottom: 44 }}>
              <div style={{
                fontSize: 11, letterSpacing: 4, color: DAR_COLORS.gold,
                textTransform: "uppercase", fontWeight: 400, marginBottom: 10,
              }}>
                {t("login.eyebrow")}
              </div>
              <h1 style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: 48, fontWeight: 400, lineHeight: 1.1,
                color: DAR_COLORS.textDark, letterSpacing: -1,
              }}>
                {t("login.title")}
              </h1>
              <div style={{
                marginTop: 12, height: 2, width: mounted ? 60 : 0,
                background: `linear-gradient(90deg, ${DAR_COLORS.gold}, ${DAR_COLORS.goldLight})`,
                borderRadius: 2,
                transition: "width 0.8s cubic-bezier(0.23,1,0.32,1) 0.7s",
              }} />
            </div>

            {error && (
              <div style={{
                padding: "12px 16px", marginBottom: 20,
                background: "#FFF0F0", border: "1px solid #FFCDD2",
                borderRadius: 8, fontSize: 13, color: "#C62828",
                animation: "fadeSlideUp 0.3s ease",
              }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <FieldGroup label={t("login.email_label")} delay="0.65s" mounted={mounted}>
                <InputField
                  type="email" value={email} placeholder={t("login.email_placeholder")}
                  onChange={e => setEmail(e.target.value)}
                  onFocus={() => setFocusedField("email")}
                  onBlur={() => setFocusedField(null)}
                  focused={focusedField === "email"}
                  icon={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                    </svg>
                  }
                />
              </FieldGroup>

              <FieldGroup label={t("login.password_label")} delay="0.75s" mounted={mounted}>
                <InputField
                  type={showPassword ? "text" : "password"} value={password} placeholder="••••••••"
                  onChange={e => setPassword(e.target.value)}
                  onFocus={() => setFocusedField("password")}
                  onBlur={() => setFocusedField(null)}
                  focused={focusedField === "password"}
                  icon={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0110 0v4"/>
                    </svg>
                  }
                  suffix={
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      style={{ background: "none", border: "none", cursor: "pointer",
                        color: DAR_COLORS.textLight, padding: "0 4px", lineHeight: 1 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        {showPassword
                          ? <><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></>
                          : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>
                        }
                      </svg>
                    </button>
                  }
                />
              </FieldGroup>

              <div style={{
                textAlign: "end", marginBottom: 32, marginTop: -8,
                opacity: mounted ? 1 : 0, transition: "opacity 0.6s ease 0.85s",
              }}>
                <a href="#" style={{
                  fontSize: 12, color: DAR_COLORS.goldDark,
                  textDecoration: "none", letterSpacing: 0.5,
                }}>
                  {t("login.forgot_password")}
                </a>
              </div>

              <SubmitButton loading={loading} mounted={mounted} delay="0.9s">
                {t("login.submit")}
              </SubmitButton>
            </form>

            {/* Divider */}
            <div style={{
              display: "flex", alignItems: "center", gap: 16,
              margin: "28px 0",
              opacity: mounted ? 1 : 0, transition: "opacity 0.6s ease 1s",
            }}>
              <div style={{ flex: 1, height: 1, background: `${DAR_COLORS.gold}30` }} />
              <span style={{ fontSize: 11, color: DAR_COLORS.textLight, letterSpacing: 2 }}>
                {t("common.or")}
              </span>
              <div style={{ flex: 1, height: 1, background: `${DAR_COLORS.gold}30` }} />
            </div>

            {/* Google Sign-In button */}
            <GoogleButton
              mounted={mounted}
              delay="1.05s"
              label={t("login.google_signin", { defaultValue: "Continue with Google" })}
            />

            {/* Register link */}
            <div style={{
              textAlign: "center", marginTop: 28,
              opacity: mounted ? 1 : 0, transition: "opacity 0.6s ease 1.1s",
            }}>
              <span style={{ fontSize: 13, color: DAR_COLORS.textMid }}>
                {t("login.no_account")}{" "}
              </span>
              <button
                onClick={onNavigateToRegister}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  fontSize: 13, color: DAR_COLORS.deepGreen,
                  fontWeight: 500, fontFamily: "'Jost', sans-serif",
                  textDecoration: "underline",
                  textDecorationColor: `${DAR_COLORS.gold}66`,
                  textUnderlineOffset: 3,
                }}>
                {t("login.create_account")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function FieldGroup({ label, children, delay, mounted }) {
  return (
    <div style={{
      marginBottom: 20,
      opacity: mounted ? 1 : 0,
      transform: mounted ? "translateY(0)" : "translateY(16px)",
      transition: `all 0.7s cubic-bezier(0.23,1,0.32,1) ${delay}`,
    }}>
      <label style={{
        display: "block", marginBottom: 8,
        fontSize: 11, letterSpacing: 2, color: DAR_COLORS.textMid,
        textTransform: "uppercase", fontWeight: 400,
      }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function InputField({ type, value, placeholder, onChange, onFocus, onBlur, focused, icon, suffix }) {
  return (
    <div style={{
      display: "flex", alignItems: "center",
      border: `1.5px solid ${focused ? DAR_COLORS.gold : `${DAR_COLORS.gold}40`}`,
      borderRadius: 10,
      background: focused ? `${DAR_COLORS.gold}08` : DAR_COLORS.white,
      transition: "all 0.3s ease",
      boxShadow: focused ? `0 0 0 3px ${DAR_COLORS.gold}18` : "none",
      overflow: "hidden",
    }}>
      <div style={{
        padding: "0 14px",
        color: focused ? DAR_COLORS.gold : DAR_COLORS.textLight,
        transition: "color 0.3s ease",
        display: "flex", alignItems: "center",
      }}>
        {icon}
      </div>
      <input
        type={type} value={value} placeholder={placeholder}
        onChange={onChange} onFocus={onFocus} onBlur={onBlur}
        style={{
          flex: 1, padding: "14px 0",
          border: "none", outline: "none", background: "transparent",
          fontSize: 14, color: DAR_COLORS.textDark,
          fontFamily: "'Jost', sans-serif", letterSpacing: 0.3,
        }}
      />
      {suffix && <div style={{ paddingInlineEnd: 14 }}>{suffix}</div>}
    </div>
  );
}

function SubmitButton({ children, loading, mounted, delay }) {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="submit"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      disabled={loading}
      style={{
        width: "100%", padding: "16px 24px",
        background: hovered && !loading
          ? `linear-gradient(135deg, ${DAR_COLORS.midGreen}, ${DAR_COLORS.deepGreen})`
          : `linear-gradient(135deg, ${DAR_COLORS.deepGreen}, ${DAR_COLORS.midGreen})`,
        border: "none", borderRadius: 10,
        color: DAR_COLORS.white,
        fontSize: 12, letterSpacing: 3, textTransform: "uppercase",
        fontFamily: "'Jost', sans-serif", fontWeight: 400,
        cursor: loading ? "not-allowed" : "pointer",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
        transition: "all 0.4s ease",
        boxShadow: hovered && !loading
          ? `0 12px 32px ${DAR_COLORS.deepGreen}50`
          : `0 4px 16px ${DAR_COLORS.deepGreen}30`,
        transform: hovered && !loading ? "translateY(-2px)" : "translateY(0)",
        opacity: mounted ? 1 : 0,
        position: "relative", overflow: "hidden",
      }}
    >
      {hovered && !loading && (
        <div style={{
          position: "absolute", inset: 0,
          background: `linear-gradient(90deg, transparent 0%, ${DAR_COLORS.gold}22 50%, transparent 100%)`,
          backgroundSize: "200% 100%",
          animation: "shimmer 1.2s linear infinite",
        }} />
      )}
      {loading ? (
        <>
          <div style={{
            width: 16, height: 16,
            border: `2px solid ${DAR_COLORS.white}44`,
            borderTopColor: DAR_COLORS.white,
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
          }} />
          <span>{t("login.submitting")}</span>
        </>
      ) : (
        <>
          <span style={{ position: "relative", zIndex: 1 }}>{children}</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
            style={{ position: "relative", zIndex: 1 }}>
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </>
      )}
    </button>
  );
}